export default {
  hero: {
    title: 'Resource Center',
    subtitle: 'Explore compliance briefings, operations guides, delivery templates, and curated events for enterprise IT services across Asia-Pacific.',
    imageAlt: 'Resource center background image',
    searchPlaceholder: 'Search insights, documents, or events...'
  },
  tabs: {
    all: 'All Resources',
    insights: 'Industry Insights',
    downloads: 'Downloads',
    knowledge: 'Knowledge Base',
    events: 'Events & News'
  },
  insights: {
    title: 'Industry Insights',
    subtitle: 'Selected observations on data protection, managed service delivery, and cloud resilience for websites, customer support, CRM, and operations teams.',
    viewAll: 'View All Insights',
    readMore: 'Read More',
    items: [
      {
        id: 1,
        title: '2026 Singapore PDPA Priorities: Governance, DPO Ownership, and ICT Controls',
        description: 'A practical summary of PDPC materials, focused on public websites, customer support, third-party processing boundaries, and breach readiness.',
        image: '/images/resourceCenter/insight-report.jpg',
        badge: 'Compliance',
        badgeColor: 'bg-blue-600',
        date: 'Apr 10, 2026',
        readTime: '8 min read',
        author: 'Mingyu Zhang',
        authorAvatar: '/images/resourceCenter/author-zhang.png'
      },
      {
        id: 2,
        title: 'Korea PIPA 2026 in Practice: Cross-Border Handling and Delegated Processing',
        description: 'A structured review of common PIPA implementation issues for multinational teams working across websites, support channels, and regional delivery models.',
        image: '/images/resourceCenter/insight-singapore-pdpa.jpg',
        badge: 'Policy',
        badgeColor: 'bg-green-600',
        date: 'Apr 9, 2026',
        readTime: '7 min read',
        author: 'Meiling Lin',
        authorAvatar: '/images/resourceCenter/author-lin.png'
      },
      {
        id: 3,
        title: 'Cloud Resilience Baseline: Multi-Region Recovery and Service Restoration Order',
        description: 'A practical guide to recovery priorities, runbooks, and resilience decisions for websites, customer service channels, notifications, and CRM platforms.',
        image: '/images/resourceCenter/insight-korea-pipa.jpg',
        badge: 'Architecture',
        badgeColor: 'bg-red-600',
        date: 'Apr 12, 2026',
        readTime: '9 min read',
        author: 'Minsu Kim',
        authorAvatar: '/images/resourceCenter/author-kim.png'
      }
    ]
  },
  downloads: {
    title: 'Downloads',
    subtitle: 'Download detailed PDF checklists and templates for internal reviews, customer reporting, and managed service transitions.',
    viewAll: 'View All Materials',
    action: 'Download',
    items: [
      {
        id: 1,
        title: 'APAC Data Protection Readiness Checklist',
        description: 'A practical checklist for websites, support operations, CRM, marketing automation, and third-party SaaS governance.',
        badge: 'Checklist',
        badgeClass: 'text-blue-600 bg-blue-50',
        iconBg: 'bg-blue-100 text-blue-600',
        iconClass: 'fas fa-list-check text-blue-600',
        date: 'Updated in Apr 2026',
        fileInfo: 'PDF checklist'
      },
      {
        id: 2,
        title: 'Multi-Region Disaster Recovery Runbook Template',
        description: 'A ready-to-use template covering recovery priorities, communications, approval paths, and drill reviews.',
        badge: 'Template',
        badgeClass: 'text-green-600 bg-green-50',
        iconBg: 'bg-green-100 text-green-600',
        iconClass: 'fas fa-file-lines text-green-600',
        date: 'Updated in Apr 2026',
        fileInfo: 'PDF runbook template'
      },
      {
        id: 3,
        title: 'Managed Service Transition and Go-Live Checklist',
        description: 'A handover checklist for new projects, supplier transitions, operational takeover, and pre-launch acceptance.',
        badge: 'Delivery',
        badgeClass: 'text-purple-600 bg-purple-50',
        iconBg: 'bg-purple-100 text-purple-600',
        iconClass: 'fas fa-clipboard-check text-purple-600',
        date: 'Updated in Apr 2026',
        fileInfo: 'PDF transition checklist'
      }
    ]
  },
  knowledge: {
    title: 'Knowledge Base',
    subtitle: 'Short practical documents focused on platform operations, incident triage, security reviews, and managed service execution.',
    viewAll: 'View All Documents',
    readDoc: 'Read Document',
    categories: {
      all: 'All Categories',
      operation: 'Operations',
      troubleshooting: 'Troubleshooting',
      compliance: 'Compliance',
      security: 'Security'
    },
    hotTitle: 'Popular Technical Documents',
    hotSubtitle: 'Built around production operations, recovery, and audit readiness.',
    operationTitle: 'Operations',
    troubleshootingTitle: 'Troubleshooting',
    hotDocuments: [
      {
        id: 1,
        title: 'Kubernetes Control Plane and etcd Backup/Restore Runbook',
        description: 'Focused on kubeadm clusters, etcd snapshots, control plane validation, and service entry checks for production teams.',
        category: 'Operations',
        categoryClass: 'bg-blue-50 text-blue-600',
        iconBg: 'bg-blue-100 text-blue-600',
        iconClass: 'fas fa-server text-blue-600',
        views: '1.9k views',
        date: 'Apr 2026',
        comments: '18 discussions'
      },
      {
        id: 2,
        title: 'Distributed Incident Triage and Communication Matrix',
        description: 'A practical operating method to align technical triage, business impact assessment, and outward communication during service incidents.',
        category: 'Troubleshooting',
        categoryClass: 'bg-red-50 text-red-600',
        iconBg: 'bg-red-100 text-red-600',
        iconClass: 'fas fa-bug text-red-600',
        views: '1.6k views',
        date: 'Apr 2026',
        comments: '12 discussions'
      },
      {
        id: 3,
        title: 'Managed Service Security Review Checklist',
        description: 'A compact review list for permissions, logging, change control, and third-party access in managed environments.',
        category: 'Compliance',
        categoryClass: 'bg-green-50 text-green-600',
        iconBg: 'bg-green-100 text-green-600',
        iconClass: 'fas fa-shield-alt text-green-600',
        views: '1.3k views',
        date: 'Apr 2026',
        comments: '9 discussions'
      }
    ],
    operationTips: [
      'Validate backups, certificates, and restore dependencies before control plane changes',
      'Keep deployment, rollback, and change records inside the same on-call handbook',
      'Define separate backup and validation cycles for databases, object storage, and queues',
      'Design log retention for troubleshooting, audits, and customer-facing reporting'
    ],
    troubleshootingTips: [
      'Break incidents into customer touchpoints, control plane, data plane, and external dependencies',
      'Assign one incident owner, one business coordinator, and one external update cadence',
      'Drill DNS, certificate, notification, and ticketing recovery, not only system restoration',
      'Capture blockers, manual steps, and supplier dependencies during every postmortem'
    ]
  },
  events: {
    title: 'Events & News',
    subtitle: 'Recaps, notes, and practical takeaways from completed sessions around compliance, platform operations, and service resilience.',
    viewAll: 'View All Events',
    featured: {
      badge: 'Session Recap',
      title: '2026 APAC Data Resilience and Compliance Roundtable Recap',
      description: 'A recap of the discussion outcomes, trade-offs, and follow-up actions shared by IT leaders, DPOs, and platform teams.',
      date: 'Mar 12, 2026',
      location: 'Singapore / Online Hybrid',
      audience: 'IT leaders, DPOs, platform teams',
      status: 'Completed',
      metaNote: 'Session notes and related materials are archived',
      action: 'View Recap'
    },
    recentTitle: 'Recent Recaps',
    recentSubtitle: 'All items below are completed sessions with recap notes and archived materials.',
    recentItems: [
      {
        id: 1,
        day: '26',
        month: 'Mar',
        dateBg: 'bg-blue-100 text-blue-700',
        badge: 'Recap',
        badgeClass: 'bg-blue-50 text-blue-700',
        title: 'Singapore PDPA and Breach Notification Practice Recap',
        icon: 'far fa-clock',
        info: 'Online session · Notes published',
        action: 'View Recap',
        linkClass: 'text-blue-600 hover:text-blue-700'
      },
      {
        id: 2,
        day: '05',
        month: 'Mar',
        dateBg: 'bg-green-100 text-green-700',
        badge: 'Workshop Recap',
        badgeClass: 'bg-green-50 text-green-700',
        title: 'Kubernetes Control Plane and etcd Backup Drill Recap',
        icon: 'fas fa-map-marker-alt',
        info: 'Shanghai · Hands-on summary',
        action: 'View Recap',
        linkClass: 'text-green-600 hover:text-green-700'
      },
      {
        id: 3,
        day: '20',
        month: 'Feb',
        dateBg: 'bg-purple-100 text-purple-700',
        badge: 'Roundtable Recap',
        badgeClass: 'bg-purple-50 text-purple-700',
        title: 'Regional DR Drill Design Roundtable Recap',
        icon: 'far fa-clock',
        info: 'Online roundtable · Summary archived',
        action: 'View Recap',
        linkClass: 'text-purple-600 hover:text-purple-700'
      },
      {
        id: 4,
        day: '16',
        month: 'Jan',
        dateBg: 'bg-red-100 text-red-700',
        badge: 'Briefing Recap',
        badgeClass: 'bg-red-50 text-red-700',
        title: 'Managed Service Transition and SLA Communication Briefing Recap',
        icon: 'fas fa-map-marker-alt',
        info: 'Online briefing · Materials archived',
        action: 'View Recap',
        linkClass: 'text-red-600 hover:text-red-700'
      }
    ]
  },
  contactCta: {
    badge: 'Contact Us',
    title: 'Need material and guidance tailored to your actual business scenario?',
    subtitle: 'If you are planning website delivery, regional operations, managed services, disaster recovery drills, or customer support optimization, contact us directly. We can align the most relevant materials, implementation priorities, and regional support options to your use case.',
    highlights: [
      'Website and support consultation',
      'Regional delivery and operations alignment',
      'Document requests and solution discussion'
    ],
    note: 'Support available for Mainland China, Singapore, Japan, Korea, and other APAC operating scenarios.',
    action: 'Go to Contact'
  }
}
